// ============================================================================
// Vista — Personalizar tienda (constructor visual).
//
// Flujo de datos: BD -> EditorState -> Preview  (edición local, sin guardar)
//                EditorState -> BD              (al pulsar "Guardar").
// Tras guardar, el estado se REHIDRATA desde lo persistido (BD -> EditorState
// -> Preview) y se refresca el estado de publicación ("Guardar" vs
// "Publicar": guardar persiste; publicar genera el snapshot que ve el
// storefront).
//
// Layout: CONTROLES | EDITOR(PREVIEW). El preview usa componentes reales en
// un marco de dispositivo (desktop/tablet/mobile) y permite seleccionar
// elementos con un clic para editar sus opciones.
// ============================================================================

import { mostrarToast } from '@plataforma/ui';
import { escaparHtml, tieneFeature } from '@plataforma/utils';

import '../../styles/estilos-preview.css';

import {
  subirFaviconEmpresa,
  subirLogoBlancoEmpresa,
  subirLogoEmpresa,
} from '../api/almacenamiento.js';
import { listarBanners } from '../api/banners.js';
import { listarBloquesPagina } from '../api/bloques-pagina.js';
import { listarCategorias } from '../api/categorias.js';
import { guardarEditorTienda, obtenerEditorTienda } from '../api/editor-tienda.js';
import {
  actualizarNombreEmpresa,
  actualizarPerfilEmpresa,
  normalizarPerfilEmpresa,
  obtenerEmpresaActual,
} from '../api/empresa-perfil.js';
import { obtenerEstadoPublicacion } from '../api/estado-publicacion.js';
import { listarProductos } from '../api/productos.js';
import { publicarTienda } from '../api/publicaciones.js';
import { guardarTemaVisual, obtenerTemaVisual } from '../api/tema.js';
import {
  cablearControles,
  renderControlesElementos,
  renderControlesEstilo,
  renderControlesIdentidad,
} from '../editor/controles.js';
import { EditorState } from '../editor/estado-editor.js';
import { renderPreview } from '../editor/preview.js';
import { DISPOSITIVOS, aplicarViewportDispositivo } from '../editor/viewport-editor.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

export async function vistaPersonalizarTienda(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/personalizar-tienda');
  contenido.innerHTML = `<p class="pf-texto-suave">Cargando editor…</p>`;

  const [
    { data: empresa, error: errorEmpresa },
    { data: tema, error: errorTema },
    { data: editor, error: errorEditor },
    { data: bloquesRaw, error: errorBloques },
    { data: bannersRaw, error: errorBanners },
    { data: categoriasRaw, error: errorCategorias },
    { data: productosRaw, error: errorProductos },
  ] = await Promise.all([
    obtenerEmpresaActual(),
    obtenerTemaVisual(),
    obtenerEditorTienda(),
    listarBloquesPagina(),
    listarBanners(),
    listarCategorias(),
    listarProductos({ estado: 'publicado' }),
  ]);

  if (
    errorEmpresa ||
    errorTema ||
    errorEditor ||
    errorBloques ||
    errorBanners ||
    errorCategorias ||
    errorProductos
  ) {
    contenido.innerHTML = `<p class="pf-campo__error">No se pudo cargar el editor de tu tienda.</p>`;
    return;
  }

  const estado = new EditorState({
    tema,
    editor,
    empresa,
    tieneFeature,
  });
  estado._elementoSeleccionado = null;

  const plan = obtenerPlanEfectivoNombre(empresa);
  const hrefMejorarPlan = '#/cambiar-plan';

  // Valores de identidad al montar: se usan para persistir solo lo que cambió.
  // `empresa.perfil` viene normalizado por obtenerEmpresaActual; se vuelve a
  // pasar por el mismo normalizador para no depender de la forma del embed
  // (objeto en 1:1, array to-many, o null sin fila de perfil).
  const identidadInicial = {
    nombre: empresa.nombre ?? '',
    perfil: { ...normalizarPerfilEmpresa(empresa?.empresa_perfil ?? empresa?.perfil) },
  };

  // Contenido REAL de la portada (bloques, banners, categorías y productos):
  // el preview lo renderiza tal cual, sin sustituir nada por contenido demo.
  // Los filtros reflejan lo que `publicar_tienda` publica (banners activos,
  // categorías no ocultas, productos publicados).
  const contenidoTienda = {
    bloques: (bloquesRaw ?? []).map((bloque) => ({
      id: bloque.id,
      tipo: bloque.tipo_bloque?.codigo ?? null,
      // `estado` es necesario: el render del storefront (contenidoHomeHtml)
      // solo muestra bloques con estado === 'activo'. Sin él, el preview no
      // renderiza ninguna sección de la portada.
      estado: bloque.estado,
      configuracion: bloque.configuracion ?? {},
    })),
    banners: (bannersRaw ?? []).filter((banner) => banner.estado === 'activo'),
    categorias: (categoriasRaw ?? []).filter((categoria) => categoria.estado !== 'oculta'),
    productos: productosRaw ?? [],
  };

  const subirIdentidad = {
    logo: (archivo) => subirLogoEmpresa(empresa.id, archivo),
    logo_blanco: (archivo) => subirLogoBlancoEmpresa(empresa.id, archivo),
    favicon: (archivo) => subirFaviconEmpresa(empresa.id, archivo),
  };

  // ---- Marcado base -----------------------------------------------------
  contenido.innerHTML = `
    <div class="pf-ed" data-editor>
      <div class="pf-ed__topbar">
        <div>
          <h1 class="pf-ed__titulo">Personalizar tienda</h1>
          <p class="pf-ed__subtitulo">Diseña la identidad y la portada de tu tienda con preview en tiempo real.</p>
        </div>
        <div class="pf-ed__topbar-acciones">
          <span class="pf-badge-estado" tono="neutral">Plan ${escaparHtml(plan)}</span>
          <span class="pf-ed__estado" id="pf-ed-publicacion"></span>
          <button type="button" class="pf-boton pf-boton--primario" id="pf-ed-guardar">Guardar cambios</button>
        </div>
      </div>

      <div class="pf-ed__cuerpo">
        <aside class="pf-ed__panel" id="pf-ed-panel">
          <div class="pf-ed__tabs" role="tablist">
            <button type="button" class="pf-ed__tab pf-ed__tab--activo" data-tab="identidad">Identidad</button>
            <button type="button" class="pf-ed__tab" data-tab="elementos">Elementos</button>
            <button type="button" class="pf-ed__tab" data-tab="estilos">Estilos</button>
          </div>
          <div class="pf-ed__tabcontenido" id="pf-ed-controles"></div>
        </aside>

        <section class="pf-ed__escenario">
          <div class="pf-ed__dispositivos" role="group" aria-label="Dispositivo">
            ${DISPOSITIVOS.map(
              (d, i) => `
              <button type="button" class="pf-ed__dispositivo${i === 0 ? ' pf-ed__dispositivo--activo' : ''}" data-dispositivo="${d.id}" title="${d.nombre}">
                <span class="pf-ed__dispositivo-label">${d.nombre}</span>
              </button>`,
            ).join('')}
          </div>
          <div class="pf-ed__vistaprevia" id="pf-ed-vistaprevia">
            <div class="pf-ed__marco pf-ed__marco--desktop" data-marco="desktop">
              <div class="pf-ed__navegador"><span class="pf-ed__navegador-dot"></span><span class="pf-ed__navegador-dot"></span><span class="pf-ed__navegador-dot"></span></div>
              <div class="pf-ed__lienzo" id="pf-ed-lienzo-desktop">
                <div class="pf-ed__viewport" data-viewport="desktop"></div>
              </div>
            </div>
            <div class="pf-ed__marco pf-ed__marco--tablet" data-marco="tablet" hidden>
              <div class="pf-ed__lienzo" id="pf-ed-lienzo-tablet">
                <div class="pf-ed__viewport" data-viewport="tablet"></div>
              </div>
            </div>
            <div class="pf-ed__marco pf-ed__marco--mobile" data-marco="mobile" hidden>
              <div class="pf-ed__lienzo" id="pf-ed-lienzo-mobile">
                <div class="pf-ed__viewport" data-viewport="mobile"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  `;

  const lienzos = {
    desktop: contenido.querySelector('#pf-ed-lienzo-desktop'),
    tablet: contenido.querySelector('#pf-ed-lienzo-tablet'),
    mobile: contenido.querySelector('#pf-ed-lienzo-mobile'),
  };
  const viewports = {
    desktop: contenido.querySelector('[data-viewport="desktop"]'),
    tablet: contenido.querySelector('[data-viewport="tablet"]'),
    mobile: contenido.querySelector('[data-viewport="mobile"]'),
  };
  let dispositivoActivo = 'desktop';
  // Cuando el usuario selecciona desde el panel "Elementos", la preview debe
  // desplazarse y centrar la sección. Este flag se consume una sola vez en el
  // siguiente render de la preview (nunca en los re-renders por edición).
  let desplazarElemento = false;

  function seleccionarElemento(id, opciones = {}) {
    estado._elementoSeleccionado = id;
    desplazarElemento = opciones.desplazar === true;
    pintarControles();
    pintarPreview();
  }

  // Solo se renderiza el dispositivo activo: su <iframe srcdoc> vive en un
  // VIEWPORT VIRTUAL de ancho lógico (aplicarViewportDispositivo) y se
  // escala visualmente para caber. Las media queries reales del storefront
  // evalúan contra el ancho lógico, no contra el ancho físico del panel.
  function pintarPreview() {
    const viewport = viewports[dispositivoActivo];
    if (viewport) {
      renderPreview(
        viewport,
        estado,
        contenidoTienda,
        estado._elementoSeleccionado,
        seleccionarElemento,
        { desplazar: desplazarElemento },
      );
    }
    desplazarElemento = false;
  }

  // Recalcula la escala visual del viewport lógico según el espacio libre
  // del lienzo (por ejemplo al redimensionar el panel o cambiar de pestaña).
  function recalcularEscala() {
    aplicarViewportDispositivo({
      lienzo: lienzos[dispositivoActivo],
      viewport: viewports[dispositivoActivo],
      dispositivo: dispositivoActivo,
    });
  }

  function pintarControles() {
    const panel = contenido.querySelector('#pf-ed-controles');
    const tabActivo = contenido.querySelector('.pf-ed__tab--activo')?.dataset.tab ?? 'identidad';

    // Conserva el foco y el caret del campo "Nombre de tienda" mientras se
    // re-renderiza: el nombre se refleja en vivo y el re-render no debe
    // interrumpir la escritura.
    const enfocado = panel.querySelector('[data-identidad="nombre"]');
    const estabaFocused = enfocado === document.activeElement;
    const caret = estabaFocused ? enfocado.selectionStart : null;

    let html;
    if (tabActivo === 'estilos') {
      html = renderControlesEstilo(estado, tieneFeature, hrefMejorarPlan);
    } else if (tabActivo === 'identidad') {
      html = renderControlesIdentidad(estado, tieneFeature, hrefMejorarPlan);
    } else {
      html = renderControlesElementos(estado, tieneFeature, hrefMejorarPlan);
    }

    panel.innerHTML = html;
    cablearControles(panel, estado, {
      subirIdentidad,
      // Panel -> preview: elegir un elemento lo selecciona y desplaza la vista
      // previa hasta él (misma fuente de verdad que el clic en la preview).
      onSeleccionarElemento: (id) => seleccionarElemento(id, { desplazar: true }),
    });

    if (estabaFocused) {
      const nuevo = panel.querySelector('[data-identidad="nombre"]');
      if (nuevo) {
        nuevo.focus();
        if (caret !== null) nuevo.setSelectionRange(caret, caret);
      }
    }
  }

  // ---- Estado de publicación (Guardar vs Publicar) ----------------------
  // "Guardar" persiste en la BD; "Publicar" genera el snapshot versionado
  // que consume el storefront. Este bloque hace visible esa diferencia y
  // ofrece el botón "Publicar cambios" reutilizando la MISMA función del
  // dashboard (api/publicaciones.js), sin duplicar lógica.
  function pintarEstadoPublicacion({ publicada, pendiente }) {
    const nodo = contenido.querySelector('#pf-ed-publicacion');
    if (!nodo) return;

    if (publicada && !pendiente) {
      nodo.innerHTML = '<pf-badge-estado tono="exito">✓ Publicado</pf-badge-estado>';
      return;
    }

    nodo.innerHTML = `
      <pf-badge-estado tono="alerta">Hay cambios pendientes de publicar.</pf-badge-estado>
      <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-ed-publicar-cambios">
        Publicar cambios
      </button>`;

    nodo.querySelector('#pf-ed-publicar-cambios')?.addEventListener('click', publicarCambios);
  }

  async function refrescarEstadoPublicacion() {
    const { publicada, pendiente } = await obtenerEstadoPublicacion();
    pintarEstadoPublicacion({ publicada, pendiente });
  }

  async function publicarCambios() {
    const boton = contenido.querySelector('#pf-ed-publicar-cambios');
    if (!boton) return;
    boton.disabled = true;
    boton.textContent = 'Publicando…';

    const { error } = await publicarTienda(empresa.id);
    if (error) {
      mostrarToast(error.message ?? 'No se pudo publicar.', { tono: 'peligro' });
    } else {
      mostrarToast('✓ Tienda publicada correctamente.', { tono: 'exito' });
    }

    await refrescarEstadoPublicacion();
  }

  // Re-pintar preview al cambiar el estado local (sin tocar BD).
  estado.suscribir(() => {
    pintarPreview();
    pintarControles();
  });

  // ---- Tabs -------------------------------------------------------------
  contenido.querySelectorAll('.pf-ed__tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      contenido
        .querySelectorAll('.pf-ed__tab')
        .forEach((t) => t.classList.remove('pf-ed__tab--activo'));
      tab.classList.add('pf-ed__tab--activo');
      pintarControles();
    });
  });

  // ---- Dispositivos ------------------------------------------------------
  contenido.querySelectorAll('[data-dispositivo]').forEach((boton) => {
    boton.addEventListener('click', () => {
      contenido
        .querySelectorAll('[data-dispositivo]')
        .forEach((b) => b.classList.remove('pf-ed__dispositivo--activo'));
      boton.classList.add('pf-ed__dispositivo--activo');
      dispositivoActivo = boton.dataset.dispositivo;
      contenido.querySelectorAll('[data-marco]').forEach((marco) => {
        marco.hidden = marco.dataset.marco !== dispositivoActivo;
      });
      pintarPreview();
      recalcularEscala();
    });
  });

  // Al cambiar el tamaño del panel (redimensionar la ventana o el contenedor
  // del preview) recalcula la escala visual; el viewport lógico no cambia.
  window.addEventListener('resize', recalcularEscala);
  if ('ResizeObserver' in window) {
    const observador = new ResizeObserver(recalcularEscala);
    observador.observe(contenido.querySelector('#pf-ed-vistaprevia'));
  }

  // ---- Guardar ----------------------------------------------------------
  contenido.querySelector('#pf-ed-guardar').addEventListener('click', async () => {
    const boton = contenido.querySelector('#pf-ed-guardar');

    const { tema: nuevoTema, editor: nuevoEditor, identidad } = estado.snapshot();
    const nombre = String(identidad.nombre ?? '').trim();
    if (!nombre) {
      mostrarToast('El nombre de la tienda es obligatorio.', { tono: 'peligro' });
      return;
    }

    boton.disabled = true;
    boton.textContent = 'Guardando…';

    // El editor persiste lo estructural en `editor_tienda`; los colores y
    // detalles visuales viven en `tema_visual` (que consume el storefront),
    // y la identidad (nombre/logos/favicon) en `empresa`/`empresa_perfil`.
    const tareas = [guardarTemaVisual(nuevoTema), guardarEditorTienda(nuevoEditor)];

    const cambiosPerfil = {};
    for (const campo of ['logo_url', 'logo_blanco_url', 'favicon_url']) {
      if ((identidad.perfil[campo] ?? null) !== (identidadInicial.perfil[campo] ?? null)) {
        cambiosPerfil[campo] = identidad.perfil[campo] ?? null;
      }
    }
    if (nombre !== identidadInicial.nombre) tareas.push(actualizarNombreEmpresa(nombre));
    if (Object.keys(cambiosPerfil).length > 0) tareas.push(actualizarPerfilEmpresa(cambiosPerfil));

    const resultados = await Promise.all(tareas);

    boton.disabled = false;
    boton.textContent = 'Guardar cambios';

    const error = resultados.find((r) => r.error);
    if (error) {
      mostrarToast(error.error?.message ?? 'No se pudo guardar.', { tono: 'peligro' });
      return;
    }

    identidadInicial.nombre = nombre;
    identidadInicial.perfil = { ...identidad.perfil };

    // Rehidrata el editor DESDE lo persistido (BD -> EditorState -> Preview):
    // el preview y los controles reflejan exactamente lo que quedó guardado
    // (nombre, logos, tema y editor), no una copia en memoria. Esto cierra el
    // ciclo que hacía parecer que los cambios "desaparecían".
    const [empresaGuardada, temaGuardado, editorGuardado] = await Promise.all([
      obtenerEmpresaActual(),
      obtenerTemaVisual(),
      obtenerEditorTienda(),
    ]);
    if (!empresaGuardada.error && !temaGuardado.error && !editorGuardado.error) {
      estado.reconciliar({
        tema: temaGuardado.data,
        editor: editorGuardado.data,
        empresa: empresaGuardada.data,
      });
      identidadInicial.nombre = estado.identidad.nombre;
      identidadInicial.perfil = { ...estado.identidad.perfil };
    }

    await refrescarEstadoPublicacion();
    mostrarToast('✓ Cambios guardados', { tono: 'exito' });
  });

  // ---- Rendering inicial -------------------------------------------------
  pintarPreview();
  recalcularEscala();
  pintarControles();
  refrescarEstadoPublicacion();
}

function obtenerPlanEfectivoNombre(empresa) {
  // Nombre legible del plan efectivo (reutiliza la estructura de empresa).
  const suscripcion = empresa?.suscripcion?.find((s) => s.estado === 'activa');
  if (suscripcion?.plan?.nombre) return suscripcion.plan.nombre;
  if (empresa?.plan?.nombre) return empresa.plan.nombre;
  if (empresa?.plan_prueba?.nombre) return empresa.plan_prueba.nombre;
  return 'Sin plan';
}
